/* cart-instant.js - the instant cart (GAH-INSTANT-CART-01).

   Paul (Sep 25): "when I click Add to Cart, no matter which item, the cart
   instantly loads with the price predetermined in the exact perfect
   formatting - figure it out on the client, validate on the server after."

   How it works:
   1. PROTOTYPES. At idle, one GET /?sections=cart-instant-protos returns, for
      every variant a Benchmark button can post, the drawer row it WILL render
      as - rendered by the drawer's own Liquid, so money, image crops and copy
      are the server's by construction - plus the bundle upgrade card and a
      price catalog. Cached per session, market and currency.
   2. PREDICTION. On the click, the engine applies the posted lines to the
      current cart (window._cartState) the way Shopify will: merge a line with
      the same variant, plan and properties, otherwise prepend it; drop a
      bundle or plan the cart already owns (the dropOwned* rules in
      benchmark-pdp.js); size shipping protection from the real variant table.
      Totals are the server's current total plus the delta of what changed, so
      lines this page knows nothing about never skew the number.
   3. RENDER on the click frame: the finished rows, the upgrade card, Total,
      the SAVE pill, the shipping-protection line and the header count.
   4. VALIDATE. The add.js response carries the server's drawer and the whole
      cart (sections/cart-instant-state.liquid). The drawer swaps in, which is
      invisible when the prediction was right, and any difference is reported
      as a drift (console + dataLayer 'instant_cart_drift') so a change on the
      Liquid side that the prototypes did not follow shows up as a number.

   Nothing here posts to the cart. benchmark-pdp.js and benchmark-bundle-cart.js
   own the requests and call begin() / settle(); a missing prototype makes
   begin() return null and the caller falls back to its old path. */
(() => {
  if (window.InstantCart) return;

  const SECTION = 'cart-instant-protos';
  const STATE_SECTION = 'cart-instant-state';
  // v2: prototypes cached before the Canada upgrade card baked its mat id (GAH-QUICKWINS-01 T-11)
  const STORE_PREFIX = 'gd-instant-cart-v2';
  const TTL_MS = 30 * 60 * 1000;
  const CHECKOUT_WAIT_MS = 4000;
  const BEFORE_CHECKOUT_MS = 1500;
  const ROWGROUP = '#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]';
  /* cart-drawer.liquid's upgrade-card test: any bundle-tagged line or bundle
     product counts as "has the bundle"; one of these mats alone is a solo mat */
  const BUNDLE_HANDLES = [
    'benchmarkgolf-launch-bundle',
    'benchmarkgolf-launch-bundle-plus',
    'the-benchmark-launch-bundle',
    'the-benchmark-launch-bundle-1',
    'the-benchmark-launch-bundle-218',
    // GAH-CA-BUNDLE-01: the CA free-shipping twin; only ever in a CA cart
    'the-ultimate-golf-gift-bundle-ca',
  ];
  const SOLO_MAT_HANDLES = ['golf-at-home', 'golf-at-home-redesign', 'golf-at-home-149', 'golf-at-home-179'];
  /* ...plus the Canada / Australia / UK twin, in its own market only - cart-drawer.liquid
     gates it on localization.country the same way */
  const TWIN_MAT_BY_COUNTRY = { CA: 'golf-at-home-canada', AU: 'golf-at-home-au', GB: 'golf-at-home-uk' };
  const isSoloMat = (line) =>
    (SOLO_MAT_HANDLES.includes(line.handle) || line.handle === TWIN_MAT_BY_COUNTRY[(window.Shopify || {}).country] || line.handle === 'golf-at-home-eu') &&
    !(line.properties && line.properties._bm_bundle);
  // the handles cart-drawer.liquid draws as the Launch Bundle card by handle (bmb_is_main)
  const BMB_MAIN_HANDLES = [
    'benchmarkgolf-launch-bundle',
    'benchmarkgolf-launch-bundle-plus',
    'the-benchmark-launch-bundle',
    'the-ultimate-golf-gift-bundle-ca',
  ];

  let book = null;
  let loading = null;
  let pending = null;
  let seq = 0;
  const warmed = [];
  const replay = [];

  /* ---------- prototypes ---------- */

  /* On a product page the section is requested from the page's own URL, so it
     also renders that product's rows (whatever the page sells is covered);
     everywhere else from the root. */
  const protoPath = () => (/\/products\/[^/]+/.test(window.location.pathname) ? window.location.pathname : null);

  const storageKey = () => {
    const s = window.Shopify || {};
    return [STORE_PREFIX, s.theme && s.theme.id, s.country, s.currency && s.currency.active, s.locale, protoPath() || '/'].join('|');
  };

  const readStore = () => {
    try {
      const raw = window.sessionStorage.getItem(storageKey());
      if (!raw) return null;
      const stored = JSON.parse(raw);
      return stored && Date.now() - stored.t < TTL_MS ? stored.html : null;
    } catch (error) {
      return null;
    }
  };

  const writeStore = (html) => {
    try {
      window.sessionStorage.setItem(storageKey(), JSON.stringify({ t: Date.now(), html }));
    } catch (error) {
      /* private mode or a full quota: the book still lives for this page */
    }
  };

  const parse = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.getElementById('cart-instant-protos');
    if (!root) return null;
    let catalog;
    try {
      catalog = JSON.parse(root.querySelector('script[data-ic-catalog]').textContent);
    } catch (error) {
      return null;
    }
    const rows = {};
    root.querySelectorAll('template[data-ic-row]').forEach((t) => {
      rows[t.dataset.icRow] = t.innerHTML;
    });
    /* GAH-GIFTPASS-01: a second group card (mat, pad, Gift Pass) rides beside
       the subscription one, so a gift buyer's bundle in arm B lands on the
       click frame like everyone else's */
    const groups = [...root.querySelectorAll('template[data-ic-group]')].map((t) => ({
      key: t.dataset.icGroup,
      html: t.innerHTML,
      ids: t.dataset.icGroupIds.split(',').map(Number),
      // GAH-GIFT-SEAT-01: a card that shares its variants with another names its plan
      plan: Number(t.dataset.icGroupPlan) || 0,
    }));
    const upgrade = root.querySelector('template[data-ic-upgrade]');
    return {
      rows,
      catalog,
      group: groups[0] || null,
      groups,
      upgrade: upgrade ? upgrade.innerHTML.trim() : '',
      currency: root.dataset.currency,
    };
  };

  /* the prototype rows are only instant if their pictures are already
     decoded: fetch every crop they reference and keep the decoded images
     referenced, so the inserted <img> paints on the frame it lands */
  const warm = (b) => {
    const urls = new Set();
    const collect = (html) => {
      html.replace(/(?:src|srcset)="([^"]+)"/g, (match, value) => {
        value.split(',').forEach((part) => {
          const url = part.trim().split(/\s+/)[0];
          if (url && url.indexOf('data:') !== 0) urls.add(url.replace(/&amp;/g, '&'));
        });
        return match;
      });
    };
    Object.values(b.rows).forEach(collect);
    (b.groups || []).forEach((g) => collect(g.html));
    collect(b.upgrade);
    urls.forEach((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      warmed.push(img);
      if (img.decode) img.decode().catch(() => {});
    });
  };

  const accept = (b) => {
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!b || (active && b.currency && b.currency !== active)) return null;
    book = b;
    checkMoney(b);
    warm(b);
    return b;
  };

  /* the drawer's page-load request carries the prototypes (cart-drawer.js
     initCartDrawerDefaultState): take them, fresher than any cached copy */
  const ingest = (html) => {
    const b = accept(parse(html));
    if (b) writeStore(html);
    return b;
  };

  const load = () => {
    if (book) return Promise.resolve(book);
    if (loading) return loading;
    const cached = readStore();
    if (cached && accept(parse(cached))) return Promise.resolve(book);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    loading = fetch(`${protoPath() || root}?sections=${SECTION}`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        const html = json && json[SECTION];
        if (!html) return null;
        const b = accept(parse(html));
        if (b) writeStore(html);
        return b;
      })
      .catch(() => null)
      .finally(() => {
        loading = null;
      });
    return loading;
  };

  /* ---------- money, formatted the way Liquid does ---------- */

  const moneyFormat = () => (book && book.catalog.moneyFormat) || window.money_format || '${{amount}}';

  /* Liquid money_without_trailing_zeros. With a no-decimals shop format it
     ROUNDS ($123.50 prints $124); formatMoneyWithoutTrailingZeros would print
     the cents instead, which is how a $285.25 cart used to flicker. */
  const moneyNoZeros = (cents) => {
    const format = moneyFormat();
    if (/\{\{\s*amount_no_decimals/.test(format) && typeof formatMoney === 'function') return formatMoney(cents, format);
    if (typeof formatMoneyWithoutTrailingZeros === 'function') return formatMoneyWithoutTrailingZeros(cents);
    return `$${Math.round(cents / 100)}`;
  };

  /* Liquid money. Nothing in the drawer prints through it any more: the bundle
     card moved to money_without_trailing_zeros (GAH-CA-CAD), because under the
     CAD format "${{amount}}" plain money shows "$228.00" to Canadians only. Kept
     on the public API for callers outside this file. */
  const money = (cents) => (typeof formatMoney === 'function' ? formatMoney(cents, moneyFormat()) : moneyNoZeros(cents));

  let moneyExact = true;
  const checkMoney = (b) => {
    const samples = b.catalog.samples || {};
    moneyExact = Object.keys(samples).every((cents) => {
      const want = samples[cents];
      const got = moneyNoZeros(Number(cents));
      if (got !== want) drift('money', want, got);
      return got === want;
    });
  };

  /* ---------- drift reporting ---------- */

  const drift = (where, expected, actual) => {
    const clip = (v) => String(v == null ? '' : v).slice(0, 120);
    try {
      console.warn('[instant-cart] drift', where, { expected, actual });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'instant_cart_drift',
        ic_where: where,
        ic_expected: clip(expected),
        ic_actual: clip(actual),
      });
    } catch (error) {
      /* reporting never breaks the cart */
    }
  };

  /* ---------- the cart model ---------- */

  const discounted = (cart) =>
    !!cart &&
    ((cart.total_discount || 0) > 0 ||
      (cart.cart_level_discount_applications || []).length > 0 ||
      (cart.items || []).some((line) => (line.line_level_discount_allocations || []).length > 0));

  const isSp = (line) => {
    const handle = line.handle || '';
    return handle.includes('shipping-protection') || handle.includes('navidium');
  };

  const isIgnored = (line) =>
    typeof isIgnoredCartItem === 'function' ? isIgnoredCartItem(line) : isSp(line);

  /* excluded from the SAVE pill exactly as cart-drawer.liquid's compare loop
     excludes them */
  const isService = (line) => {
    const props = line.properties || {};
    return isSp(line) || !!props['_gwp-product'] || !!props._cart_promo_item;
  };

  const planOf = (line) =>
    (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.id) || null;

  const sameProps = (a, b) => {
    const pa = a || {};
    const pb = b || {};
    const ka = Object.keys(pa);
    return ka.length === Object.keys(pb).length && ka.every((k) => String(pa[k]) === String(pb[k]));
  };

  const variantInfo = (id) => (book && book.catalog.variants[String(id)]) || null;

  /* a line's contribution to the SAVE pill: compare-at x qty minus what it
     costs, only when the variant has a compare-at above its price */
  const savingsOf = (line) => {
    if (isService(line)) return 0;
    /* GAH-SAVE-PILL: a bundle group's coaching line is worth what the bundle's
       compare-at leaves after the mat and pad (global.js _bundleCoachCompare) */
    const props = line.properties || {};
    const coachValue = window.GD_SUB && window.GD_SUB.coachValue;
    if (props._bm_bundle && props._bm_bundle_role !== 'main' && String(props._bm_bundle_order) === '2' && coachValue > 0) {
      return Math.max(0, coachValue * line.quantity - (line.final_line_price || 0));
    }
    const info = variantInfo(line.variant_id);
    const compare = info ? info.c : line._compare || 0;
    const price = info ? info.p : line.price;
    if (!compare || compare <= price) return 0;
    return compare * line.quantity - line.final_line_price;
  };

  const spTable = () => {
    const sp = window.ShippingProtection;
    return sp && typeof sp.findBestVariant === 'function' && typeof sp.computeProtectionCents === 'function' ? sp : null;
  };

  const spEnabled = () => {
    const sp = window.ShippingProtection;
    return !!(sp && typeof sp.isEnabled === 'function' && sp.isEnabled());
  };

  /* the same eligibility rule as ShippingProtection: ships, or is a bundle
     (whose own line says requires_shipping false while its components ship) */
  const protects = (line) => {
    const sp = window.ShippingProtection;
    if (sp && typeof sp.isProtectableLine === 'function') return sp.isProtectableLine(line);
    return !isSp(line) && (line.requires_shipping === true || line.has_components === true);
  };

  const protectableOf = (items) => {
    const products = items.filter((line) => !isSp(line));
    if (!products.some(protects)) return 0;
    return products.reduce((sum, line) => sum + (line.final_line_price || 0), 0);
  };

  /* Apply posted lines to a cart the way /cart/add.js will.
     Returns null when any line is unknown - the caller keeps its old path. */
  const predictAdd = (cart, lines) => {
    if (!book || !cart || !Array.isArray(cart.items)) return null;
    const items = cart.items.map((line) => ({ ...line, properties: { ...(line.properties || {}) } }));

    /* benchmark-pdp.js dropOwnedBundle: a bundle the cart already holds is
       dropped whole. dropOwnedSubscriptions: a plan line the cart already
       holds is dropped, unless it belongs to a bundle. */
    const ownedBundles = new Set(items.map((line) => line.properties._bm_bundle).filter(Boolean));
    let posted = lines.filter((line) => !(line.properties && line.properties._bm_bundle && ownedBundles.has(line.properties._bm_bundle)));
    posted = posted.filter((line) => {
      if (!line.selling_plan || (line.properties && line.properties._bm_bundle)) return true;
      return !items.some((have) => have.variant_id === Number(line.id) && planOf(have) === Number(line.selling_plan));
    });

    const fresh = [];
    let productDelta = 0;
    let savingsDelta = 0;
    for (const line of posted) {
      const id = Number(line.id);
      const info = variantInfo(id);
      if (!info) return null;
      const plan = line.selling_plan ? Number(line.selling_plan) : null;
      const unit = plan ? info.plans && info.plans[String(plan)] : info.p;
      if (unit === undefined || unit === null) return null;
      const quantity = Number(line.quantity) || 1;
      const props = line.properties || {};
      const existing = items.find(
        (have) => have.variant_id === id && planOf(have) === plan && sameProps(have.properties, props)
      );
      if (existing) {
        const before = savingsOf(existing);
        existing.quantity += quantity;
        existing.final_line_price += existing.final_price * quantity;
        existing.original_line_price = (existing.original_line_price || 0) + (existing.original_price || existing.final_price) * quantity;
        existing._icMerged = (existing._icMerged || 0) + quantity;
        productDelta += existing.final_price * quantity;
        savingsDelta += savingsOf(existing) - before;
      } else {
        const created = {
          id,
          variant_id: id,
          key: `ic-pending-${++seq}`,
          handle: info.h,
          quantity,
          properties: { ...props },
          price: unit,
          original_price: unit,
          final_price: unit,
          final_line_price: unit * quantity,
          original_line_price: unit * quantity,
          requires_shipping: info.s === true,
          has_components: info.k === true,
          selling_plan_allocation: plan ? { selling_plan: { id: plan } } : null,
          _icNew: true,
        };
        fresh.push(created);
        productDelta += created.final_line_price;
        savingsDelta += savingsOf(created);
      }
    }
    /* Shopify prepends new lines in posting order: the last one lands on top */
    for (let i = fresh.length - 1; i >= 0; i--) items.unshift(fresh[i]);
    return finish(cart, items, productDelta, savingsDelta, posted);
  };

  /* Remove whole lines (by key) - the drawer's upgrade card swaps the loose
     mat for the bundle, which is predictRemove + predictAdd */
  const predictRemove = (cart, keys) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const drop = new Set(keys);
    let productDelta = 0;
    let savingsDelta = 0;
    const items = [];
    cart.items.forEach((line) => {
      if (drop.has(line.key)) {
        productDelta -= line.final_line_price || 0;
        savingsDelta -= savingsOf(line);
      } else {
        items.push({ ...line, properties: { ...(line.properties || {}) } });
      }
    });
    return { items, total_price: cart.total_price + productDelta, _savingsDelta: savingsDelta, cart_level_discount_applications: cart.cart_level_discount_applications };
  };

  /* shipping protection, totals and the regions every prediction shares */
  const finish = (base, items, productDelta, savingsDelta, posted) => {
    const sp = spTable();
    const spLines = base.items.filter(isSp);
    const before = spLines[0];
    const withoutSp = items.filter((line) => !isSp(line));
    const protectable = protectableOf(withoutSp);
    let spVariant = null;
    let spUnknown = false;
    let exact = true;
    if (spEnabled() && protectable > 0) {
      spVariant = sp ? sp.findBestVariant(sp.computeProtectionCents(protectable)) : null;
      /* the variant table loads at init; before it lands the line cannot be
         named, so nothing may add, swap or remove it - the sync does that once
         it can - and Total shows the formula's figure meanwhile */
      if (!spVariant) {
        spUnknown = true;
        exact = false;
      }
    }
    // every protection line counts: a stale-cache ride can leave two until the
    // sync collapses them to one
    const spBefore = spLines.reduce((sum, line) => sum + (line.final_line_price || 0), 0);
    const spAfter = spVariant ? spVariant.price : spUnknown && sp ? sp.computeProtectionCents(protectable) : 0;
    /* the line the sync will leave in the cart, drawn nowhere (the drawer
       hides it) but counted in Total exactly as the server will count it */
    const spLine = spVariant
      ? { variant_id: spVariant.id, handle: 'shipping-protection-benchmark', quantity: 1, final_line_price: spAfter, properties: {} }
      : null;
    const nextItems = spLine ? [spLine, ...withoutSp] : withoutSp;
    /* a discount on the cart (a code, an automatic discount on a line) moves
       with the subtotal in ways the storefront cannot compute - percentage,
       thresholds, entitlements - so such a total is never predicted exactly */
    if (discounted(base)) exact = false;

    const predicted = {
      items: nextItems,
      total_price: (base.total_price || 0) + productDelta + (spAfter - spBefore),
      item_count: nextItems.reduce((n, line) => n + line.quantity, 0),
    };
    const count = typeof getRealItemCount === 'function'
      ? getRealItemCount(predicted)
      : nextItems.filter((line) => !isIgnored(line)).reduce((n, line) => n + line.quantity, 0);
    const hasBundle = nextItems.some((line) => (line.properties && line.properties._bm_bundle) || BUNDLE_HANDLES.includes(line.handle));
    const soloMat = nextItems.some(isSoloMat);
    return {
      cart: predicted,
      posted: posted || [],
      exact,
      total: predicted.total_price,
      savingsDelta,
      count,
      protectable,
      spVariant,
      spUnknown,
      /* ride shipping protection along with the add when the cart has none
         yet (the common first add); a tier swap stays with ShippingProtection */
      spRide: spVariant && !before ? { id: spVariant.id, quantity: 1 } : null,
      upgrade: !hasBundle && soloMat,
      empty: count <= 0,
    };
  };

  /* the /cart/update.js `updates` entries that leave the cart's protection line
     exactly where the plan put it - folded into a caller's own write instead
     of a separate ShippingProtection round trip afterwards */
  const spUpdates = (plan, cart) => {
    const updates = {};
    if (!plan || plan.spUnknown || !cart || !Array.isArray(cart.items)) return updates;
    const line = cart.items.find(isSp);
    const target = plan.spVariant;
    if (line && (!target || line.variant_id !== target.id)) updates[line.key] = 0;
    if (target && (!line || line.variant_id !== target.id)) updates[target.id] = 1;
    return updates;
  };

  /* ---------- rendering ---------- */

  const fill = (html, index, key) =>
    html.split('__IC_I__').join(String(index)).split('__IC_K__').join(key).split('__IC_CK__').join('');

  const fragment = (html) => document.createRange().createContextualFragment(html.trim());

  /* the rows the server will draw for this prediction: new lines get their
     prototype, merged lines are patched in place, bundle children draw
     nothing (the drawer folds them into their parent's card) */
  const renderRows = (drawer, plan) => {
    const group = drawer.querySelector(ROWGROUP);
    if (!group) return false;
    const items = plan.cart.items;
    const created = [];
    const patches = [];
    for (let i = items.length - 1; i >= 0; i--) {
      const line = items[i];
      if (line._icNew) {
        if (isSp(line) || (line.properties && line.properties._bm_bundle_parent)) continue;
        let html = book.rows[String(line.variant_id)];
        if (line.properties && line.properties._bm_bundle_role === 'main' && !BMB_MAIN_HANDLES.includes(line.handle)) {
          /* the drawer draws any other line tagged as a bundle's main line as
             the GAH-SUB-01 group card: exact only for the three-line group it
             was rendered for (the control mat); anything else (a DEAL-5 twin,
             a lone tagged line) has no exact prototype, so the old path takes it */
          html = null;
          if (plan.posted.length > 1) {
            const postedIds = plan.posted.map((p) => Number(p.id));
            const postedPlans = plan.posted.map((p) => Number(p.selling_plan) || 0);
            const card = (book.groups || []).find((g) => g.ids[0] === line.variant_id && g.ids.every((id) => postedIds.includes(id)) && (!g.plan || postedPlans.includes(g.plan)));
            html = card ? card.html : null;
          }
        }
        if (!html) return false;
        /* the index is a token, not a number: a real row already owns
           CartDrawer-Item-<n>, and getElementById-based code (cart.js
           animateItemRemoval, updateLiveRegions) must never find this one */
        created.push({ html: fill(html, `ic${seq}-${i + 1}`, line.key), index: i });
      } else if (line._icMerged) {
        const row = group.querySelector(`.cart-item[data-item-key="${CSS.escape(line.key)}"]`);
        if (!row) return false;
        patches.push({ row, line });
      }
    }
    /* every check has passed: only now touch the DOM, logging each change */
    patches.forEach(({ row, line }) => {
      const setText = (el, text) => {
        if (!el) return;
        const was = el.textContent;
        el.textContent = text;
        plan.undo.push(() => { el.textContent = was; });
      };
      const input = row.querySelector('.js-cart-item-quantity');
      if (input) {
        const was = input.value;
        input.value = String(line.quantity);
        plan.undo.push(() => { input.value = was; });
      }
      const minus = row.querySelector('button[name="minus"]');
      if (minus && line.quantity > 1 && minus.hasAttribute('disabled')) {
        minus.removeAttribute('disabled');
        minus.removeAttribute('aria-disabled');
        plan.undo.push(() => {
          minus.setAttribute('disabled', '');
          minus.setAttribute('aria-disabled', 'true');
        });
      }
      setText(row.querySelector('.cart-item__bmb-now'), moneyNoZeros(line.final_line_price));
      const info = variantInfo(line.variant_id);
      if (info && info.c > info.p) setText(row.querySelector('.cart-item__bmb-was'), moneyNoZeros(info.c * line.quantity));
      setText(row.querySelector('.cart-item__quantity-static'), `×${line.quantity}`);
    });
    /* new lines sit on top, in cart order */
    created.sort((a, b) => b.index - a.index).forEach(({ html }) => {
      const frag = fragment(html);
      const row = frag.firstElementChild;
      if (!row) return;
      row.setAttribute('data-ic-pending', '');
      group.prepend(frag);
      plan.undo.push(() => row.remove());
    });
    return true;
  };

  const renderRegions = (drawer, plan) => {
    const empty = plan.empty;
    const items = drawer.querySelector('cart-drawer-items');
    const inner = drawer.querySelector('.drawer__inner');
    const checkout = drawer.querySelector('#CartDrawer-Checkout');
    const before = {
      drawer: drawer.classList.contains('is-empty'),
      items: items && items.classList.contains('is-empty'),
      itemsDisplay: items && items.style.display,
      inner: inner && inner.classList.contains('is-empty'),
      checkout: checkout && checkout.hasAttribute('disabled'),
    };
    plan.undo.push(() => {
      drawer.classList.toggle('is-empty', before.drawer);
      if (items) {
        items.classList.toggle('is-empty', before.items);
        items.style.display = before.itemsDisplay;
      }
      if (inner) inner.classList.toggle('is-empty', before.inner);
      if (checkout) {
        if (before.checkout) checkout.setAttribute('disabled', 'disabled');
        else checkout.removeAttribute('disabled');
      }
    });
    drawer.classList.toggle('is-empty', empty);
    if (items) {
      items.classList.toggle('is-empty', empty);
      if (!empty) items.style.display = '';
    }
    if (inner) inner.classList.toggle('is-empty', empty);
    if (checkout) {
      if (empty) checkout.setAttribute('disabled', 'disabled');
      else checkout.removeAttribute('disabled');
    }

    /* the "Upgrade to the bundle" card: cart-drawer.liquid draws it right
       after <cart-drawer-items> whenever a solo mat sits in a bundle-less cart */
    const current = drawer.querySelector('.drawer__content > .bmb-upgrade:not(.bmb-upgrade--empty)');
    if (plan.upgrade && !current && book.upgrade && items) {
      const frag = fragment(book.upgrade);
      const card = frag.firstElementChild;
      if (card) {
        card.setAttribute('data-ic-pending', '');
        items.after(frag);
        plan.undo.push(() => card.remove());
      }
    } else if (!plan.upgrade && current) {
      const parent = current.parentNode;
      const next = current.nextSibling;
      current.remove();
      plan.undo.push(() => parent && parent.insertBefore(current, next));
    }

    /* shipping protection: the widget is JS-drawn; show it, priced, now */
    const slotVisible = plan.cart.items.some(protects);
    const sp = window.ShippingProtection;
    if (sp && typeof sp.ensureWidget === 'function') {
      try {
        sp.ensureWidget();
      } catch (error) {}
    }
    document.querySelectorAll('.shipping-protection-slot').forEach((slot) => {
      const was = slot.style.display;
      slot.style.display = slotVisible ? '' : 'none';
      plan.undo.push(() => { slot.style.display = was; });
    });
    if (sp && typeof sp.protectionPriceText === 'function') {
      const text = sp.protectionPriceText(plan.protectable);
      document.querySelectorAll('.js-sp-price').forEach((el) => {
        const was = el.textContent;
        el.textContent = text;
        plan.undo.push(() => { el.textContent = was; });
      });
    }
  };

  /* The two drawer regions whose presence depends on what the cart holds, for
     any cart CartOptimist is displaying - a stepper or a bin tap as much as an
     add: the "Upgrade to the bundle" card (a solo mat, no bundle) and the
     shipping-protection slot (anything that ships). */
  const syncRegions = (items) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !Array.isArray(items)) return;
    const hasBundle = items.some((line) => (line.properties && line.properties._bm_bundle) || BUNDLE_HANDLES.includes(line.handle));
    const soloMat = items.some(isSoloMat);
    const current = drawer.querySelector('.drawer__content > .bmb-upgrade:not(.bmb-upgrade--empty)');
    const drawerItems = drawer.querySelector('cart-drawer-items');
    if (!hasBundle && soloMat && !current && book && book.upgrade && drawerItems) {
      // live, not pending: its button finds the loose mat in the cart itself
      drawerItems.after(fragment(book.upgrade));
    } else if ((hasBundle || !soloMat) && current) {
      current.remove();
    }
    const ships = items.some(protects);
    const sp = window.ShippingProtection;
    if (ships && sp && typeof sp.ensureWidget === 'function') {
      try {
        sp.ensureWidget();
      } catch (error) {}
    }
    document.querySelectorAll('.shipping-protection-slot').forEach((slot) => {
      slot.style.display = ships ? '' : 'none';
    });
  };

  /* the visible text a shopper compares: rows, the upgrade card, Total and
     the SAVE pill. Measured before and after the server swap. */
  const visibleText = (root) => {
    if (!root) return '';
    const out = [];
    const walk = (node) => {
      if (node.nodeType === 3) {
        out.push(node.nodeValue);
        return;
      }
      if (node.nodeType !== 1) return;
      if (node.classList.contains('visually-hidden') || node.tagName === 'TEMPLATE' || node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      const style = getComputedStyle(node);
      if (style.display === 'none') return;
      out.push(' ');
      node.childNodes.forEach(walk);
    };
    walk(root);
    return out.join('').replace(/\s+/g, ' ').trim();
  };

  const snapshot = (drawer) => ({
    rows: visibleText(drawer.querySelector(ROWGROUP)),
    upgrade: visibleText(drawer.querySelector('.drawer__content > .bmb-upgrade:not(.bmb-upgrade--empty)')),
    totals: visibleText(drawer.querySelector('.cart-drawer__footer .totals')),
  });

  /* ---------- the click frame ---------- */

  /* Predict and draw. Returns the plan, or null when this add cannot be
     predicted exactly - the caller then keeps its legacy optimistic path. */
  const begin = (lines, options = {}) => {
    const drawer = options.drawer || document.querySelector('cart-drawer');
    const cart = window._cartState;
    if (!book || !drawer || !cart) return null;
    let base = cart;
    let savingsExtra = 0;
    if (options.removeKeys && options.removeKeys.length) {
      base = predictRemove(cart, options.removeKeys);
      if (!base) return null;
      savingsExtra = base._savingsDelta || 0;
    }
    const plan = predictAdd(base, lines);
    if (!plan) return null;
    plan.savingsDelta += savingsExtra;
    if (!plan.exact || !moneyExact) plan.approximate = true;
    plan.undo = [];
    plan.drawer = drawer;

    if (!renderRows(drawer, plan)) {
      undo(plan);
      return null;
    }
    if (options.removeKeys) {
      options.removeKeys.forEach((key) => {
        const row = drawer.querySelector(`${ROWGROUP} .cart-item[data-item-key="${CSS.escape(key)}"]`);
        if (!row) return;
        const parent = row.parentNode;
        const next = row.nextSibling;
        row.remove();
        plan.undo.push(() => parent && parent.insertBefore(row, next));
      });
    }
    renderRegions(drawer, plan);
    drawer.querySelectorAll('.totals__price-group').forEach((group) => {
      const was = group.innerHTML;
      plan.undo.push(() => { group.innerHTML = was; });
    });
    if (window.CartOptimist && typeof CartOptimist.applyPrediction === 'function') {
      CartOptimist.applyPrediction({
        cart: plan.cart,
        total: plan.total,
        savingsDelta: plan.savingsDelta,
        count: plan.count,
        protectable: plan.protectable,
        spCents: plan.spVariant ? plan.spVariant.price : 0,
      });
    }
    /* rows are always exact; a total that is not (see discounted) says so
       until the server's number lands, rather than flashing a guess */
    if (plan.approximate) {
      drawer.querySelectorAll('.cartfrawerfooterWrap .cart-drawer__footer').forEach((footer) => {
        footer.classList.add('cart-total-loading');
        plan.undo.push(() => footer.classList.remove('cart-total-loading'));
      });
      // a discount may also reprice the new line itself (the prototype cannot know)
      drawer.querySelectorAll(`${ROWGROUP} [data-ic-pending]`).forEach((row) => row.classList.add('cart-price-loading'));
    }
    plan.predictedText = snapshot(drawer);
    return plan;
  };

  /* ---------- the server answer ---------- */

  const sections = () => {
    const ids = ['cart-drawer', STATE_SECTION];
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  };

  const stateFrom = (response) => {
    const html = response && response.sections && response.sections[STATE_SECTION];
    if (!html) return null;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('script[data-cart-state]');
      if (!node) return null;
      const cart = JSON.parse(node.textContent);
      /* `cart | json` has no has_components; /cart.js does, and shipping
         protection needs it (a bundle line says requires_shipping: false) */
      const bundles = doc.querySelector('script[data-cart-components]');
      const keys = new Set(bundles ? JSON.parse(bundles.textContent) : []);
      (cart.items || []).forEach((line) => {
        if (line.has_components === undefined) line.has_components = keys.has(line.key);
      });
      return cart;
    } catch (error) {
      return null;
    }
  };

  /* After the caller has swapped the server drawer in (renderContents, which
     also runs the shipping-protection sync with the same cart). `cart` is the
     full post-add cart (stateFrom), or null when the response carried none. */
  const settle = (plan, cart, rendered = true) => {
    if (cart && Array.isArray(cart.items)) {
      window._cartState = cart;
      if (window.CartOptimist) CartOptimist.reconcile(cart, { serverFooter: rendered });
      if (typeof cacheGWPData === 'function') cacheGWPData(cart);
      document.dispatchEvent(new CustomEvent('cart:state-updated', { detail: { cart } }));
    } else if (typeof updateCartAndProductJSON === 'function') {
      updateCartAndProductJSON();
    }
    if (plan && plan.drawer && plan.approximate) {
      plan.drawer.querySelectorAll('.cart-total-loading').forEach((el) => el.classList.remove('cart-total-loading'));
    }
    if (plan) plan.undo = [];
    if (plan && plan.drawer && plan.predictedText && !plan.approximate) {
      const actual = snapshot(plan.drawer);
      ['rows', 'upgrade', 'totals'].forEach((where) => {
        if (plan.predictedText[where] !== actual[where]) drift(where, plan.predictedText[where], actual[where]);
      });
    }
    const stranded = plan && plan.drawer && plan.drawer.querySelector(`${ROWGROUP} [data-ic-pending]`);
    if (stranded) redrawWhenQuiet();
    else runReplay();
  };

  /* the prediction was wrong or the post failed: take the predicted rows and
     the upgrade card back out and let the caller restore its state */
  const undo = (plan) => {
    const steps = (plan && plan.undo) || [];
    while (steps.length) {
      try {
        steps.pop()();
      } catch (error) {
        /* a node the server render already replaced */
      }
    }
  };

  const cancel = (plan) => {
    if (plan) undo(plan);
    replay.length = 0;
  };

  /* ---------- taps on a row that is still a prediction ----------
     A predicted row has no real line key yet. A tap on its stepper or bin is
     held and replayed on the server's row once it lands, so nothing the
     shopper does in that window is lost or sent against a placeholder. */
  document.addEventListener(
    'click',
    (event) => {
      const row = event.target.closest && event.target.closest('[data-ic-pending]');
      if (!row) return;
      const control = event.target.closest('button, a, input, cart-remove-button');
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // only a tap made while this module's write is in flight is kept for its
      // answer; a stale queue would fire into some later, unrelated add
      if (!pending) return;
      if (control.closest('[data-bmb-upgrade-add]')) {
        replay.push({ selector: '.drawer__content > .bmb-upgrade:not(.bmb-upgrade--empty) [data-bmb-upgrade-add]' });
        return;
      }
      const name = control.getAttribute('name');
      let selector = null;
      if (name === 'plus' || name === 'minus') selector = `button[name="${name}"]`;
      else if (control.closest('cart-remove-button')) selector = 'cart-remove-button button';
      if (selector && row.dataset.variantId) replay.push({ variantId: row.dataset.variantId, selector });
    },
    true
  );

  const runReplay = () => {
    const queued = replay.splice(0);
    queued.forEach(({ variantId, selector }) => {
      let control = null;
      if (!variantId) {
        control = document.querySelector(`cart-drawer ${selector}`);
        if (control && control.closest('[data-ic-pending]')) control = null;
      } else {
        const row = document.querySelector(`${ROWGROUP} .cart-item[data-variant-id="${CSS.escape(variantId)}"]:not([data-ic-pending])`);
        control = row && row.querySelector(selector);
      }
      if (control) control.click();
    });
  };

  /* ---------- checkout waits for the cart it shows ----------
     The drawer form posts updates[] by line order, and the rows on screen are
     a prediction until the add lands. A Check out tap in that window waits
     for EVERY pending write - the add, then the attribution write that
     follows it - and goes through with the real rows. If a held write failed
     (the add was refused, a swap stopped half way) the tap is dropped rather
     than replayed: the drawer then shows the cart as it really is and the
     shopper decides again. Never longer than CHECKOUT_WAIT_MS. */
  let releasing = false;
  let heldFailed = false;
  let checkoutWaiting = false;

  /* Checks a caller wants made right before a held tap goes to checkout (the
     split-test attribution read-back): run together, never longer than
     BEFORE_CHECKOUT_MS, and a check that fails never blocks checkout. */
  const beforeCheckout = [];
  const runBeforeCheckout = () => {
    const checks = beforeCheckout.splice(0);
    if (!checks.length) return Promise.resolve(true);
    return Promise.race([
      Promise.all(checks.map((check) => Promise.resolve().then(check).catch(() => {}))),
      new Promise((resolve) => setTimeout(resolve, BEFORE_CHECKOUT_MS)),
    ]).then(() => true);
  };

  // resolves true when every pending write settled inside the budget
  const drain = async () => {
    const deadline = Date.now() + CHECKOUT_WAIT_MS;
    let current = pending;
    while (current) {
      const left = deadline - Date.now();
      if (left <= 0) return false;
      let settled = false;
      await Promise.race([
        current.then(() => { settled = true; }),
        new Promise((resolve) => setTimeout(resolve, left)),
      ]);
      if (!settled) return false;
      if (pending === current || !pending) return true;
      current = pending;
    }
    return true;
  };

  document.addEventListener(
    'click',
    (event) => {
      if (releasing || (!pending && !beforeCheckout.length)) return;
      const control = event.target.closest && event.target.closest('#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"]');
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // one held tap at a time, whatever re-renders the button meanwhile
      if (checkoutWaiting) return;
      checkoutWaiting = true;
      // a failure only counts against a tap that waited on the failed write
      const waitedOnWrites = !!pending;
      control.setAttribute('aria-busy', 'true');
      control.classList.add('cart-control-loading');
      const done = () => {
        checkoutWaiting = false;
        control.removeAttribute('aria-busy');
        control.classList.remove('cart-control-loading');
      };
      drain().then((settled) => {
        /* a write that failed: the cart is not what the drawer showed, so the
           tap is dropped and the shopper decides again. Out of budget with
           rows still predicted (or the add still running): the form must not
           post those rows either. Out of budget with the server's rows on
           screen: only follow-up writes are left, so checkout goes ahead. */
        if (waitedOnWrites && heldFailed) {
          heldFailed = false;
          return false;
        }
        const drawer = document.querySelector('cart-drawer');
        const predicted = drawer && drawer.querySelector('[data-ic-pending]');
        if (!settled && (predicted || window._CART_ADD_IN_PROGRESS || lineWrites > 0)) return false;
        return runBeforeCheckout();
      }).then((go) => {
        done();
        if (go !== true) return;
        const live = document.getElementById(control.id) || control;
        releasing = true;
        try {
          live.click();
        } finally {
          releasing = false;
        }
      });
    },
    true
  );

  /* every cart write this module's callers make, so checkout can wait on it;
     a promise that rejects or resolves to false marks the hold failed */
  /* options.followUp: a write that only trails a change already on the
     server (split-test attribution) - it never changes a line, so a tap that
     outlasts the budget may go ahead without it. Anything else changes lines,
     and a tap never goes ahead while one of those is still unanswered. */
  let lineWrites = 0;
  const hold = (promise, options = {}) => {
    if (!pending) heldFailed = false;
    const followUp = !!options.followUp;
    if (!followUp) lineWrites += 1;
    const mine = Promise.resolve(promise).then(
      (value) => {
        if (value === false && !followUp) heldFailed = true;
      },
      () => {
        if (!followUp) heldFailed = true;
      }
    ).finally(() => {
      if (!followUp) lineWrites -= 1;
    });
    const chained = pending ? Promise.all([pending, mine]) : mine;
    pending = chained;
    chained.finally(() => {
      if (pending === chained) pending = null;
    });
    return promise;
  };

  /* ---------- the cart it predicts from stays current ----------
     window._cartState is read once per page. A page restored from the back/
     forward cache, or a tab left while the cart changed elsewhere (another
     tab, a finished checkout), would otherwise predict from an old cart; the
     drawer and the cart are re-read when the page comes back. */
  let hiddenAt = 0;
  const refreshFromServer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.initCartDrawerDefaultState === 'function') drawer.initCartDrawerDefaultState();
    if (typeof updateCartAndProductJSON === 'function') updateCartAndProductJSON();
  };
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshFromServer();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt > 15000) refreshFromServer();
  });

  /* A predicted row the server render did not replace - renderContents keeps
     the old rows while a stepper request is in flight - is re-drawn from the
     server once the cart is quiet, so it never sits on screen with dead
     controls. */
  const redrawWhenQuiet = (tries = 0) => {
    const drawer = document.querySelector('cart-drawer');
    // another render already replaced the predicted rows: the held taps now
    // have their real rows
    if (!drawer || !drawer.querySelector(`${ROWGROUP} [data-ic-pending]`)) {
      runReplay();
      return;
    }
    if (((window._CART_REQUESTS || 0) > 0 || pending) && tries < 50) {
      setTimeout(() => redrawWhenQuiet(tries + 1), 100);
      return;
    }
    const seqAtFetch = window._CART_OPTIMISTIC_SEQ;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}?sections=${sections().join(',')}`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((rendered) => {
        // a newer change since this read went out owns the drawer now
        if (window._CART_OPTIMISTIC_SEQ !== seqAtFetch) {
          replay.length = 0;
          return;
        }
        const cart = stateFrom({ sections: rendered });
        const items = drawer.querySelector('cart-drawer-items');
        if (!rendered || !cart || !items || typeof items.smoothSectionRender !== 'function') {
          replay.length = 0;
          return;
        }
        window._cartState = cart;
        items.smoothSectionRender({ ...cart, sections: rendered });
        runReplay();
      })
      .catch(() => {
        replay.length = 0;
      });
  };

  window.InstantCart = {
    load,
    ingest,
    protoSection: () => SECTION,
    protoPath: () => protoPath(),
    ready: () => !!book,
    discounted,
    begin,
    cancel,
    settle,
    sections,
    stateFrom,
    hold,
    beforeCheckout: (check) => {
      beforeCheckout.push(check);
      return () => {
        const at = beforeCheckout.indexOf(check);
        if (at > -1) beforeCheckout.splice(at, 1);
      };
    },
    spUpdates,
    syncRegions,
    money,
    moneyNoZeros,
    variant: variantInfo,
  };

  const start = () => load();
  if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2500 });
  else window.setTimeout(start, 1200);
})();
